import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const ICON_CATEGORIES = {
  apps: ['📱', '💻', '🖥️', '⌨️', '🖱️', '🖨️', '📷', '📹', '🎥', '📞', '☎️', '📠'],
  social: ['💬', '📧', '✉️', '📮', '📬', '📭', '📨', '💌', '📩', '🗨️', '💭', '🗯️'],
  files: ['📁', '📂', '🗂️', '📄', '📃', '📋', '📊', '📈', '📉', '📌', '📍', '📎'],
  education: ['📚', '📖', '📝', '✏️', '✒️', '🖊️', '🖋️', '🖍️', '📕', '📗', '📘', '📙'],
  business: ['💼', '💳', '💰', '💵', '💴', '💶', '💷', '🏦', '🏢', '🏭', '🏪', '🏬'],
  tools: ['🔧', '🔨', '⚙️', '🛠️', '⚡', '🔥', '💡', '🔦', '🔒', '🔓', '🔑', '🗝️'],
  transport: ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚛', '🚚'],
  media: ['🎵', '🎶', '🎤', '🎧', '📻', '🎬', '🎭', '🎨', '🎪', '🎯', '🎲', '🎰'],
  sports: ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🏓', '🏸'],
  food: ['🍕', '🍔', '🍟', '🌭', '🍿', '🧂', '🥗', '🍝', '🍜', '🍲', '🍱', '🍛'],
  symbols: ['✅', '❌', '⭐', '❤️', '💙', '💚', '💛', '🧡', '💜', '🖤', '🤍', '🤎'],
  arrows: ['➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '🔄', '🔃']
};

export default function IconPicker({ value, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [customIcon, setCustomIcon] = useState('');
  const [activeTab, setActiveTab] = useState('apps');

  const handleSelect = (icon) => {
    onChange(icon);
    setOpen(false);
  };

  const handleCustom = () => {
    if (customIcon.trim()) {
      onChange(customIcon.trim());
      setCustomIcon('');
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex gap-2">
        <DialogTrigger asChild>
          <Button type="button" variant="outline" className="flex-1">
            {value ? (
              value.startsWith('data:') || value.startsWith('http') ? (
                <img src={value} alt="Icon" className="w-6 h-6 object-cover rounded mr-2" />
              ) : (
                <span className="text-2xl mr-2">{value}</span>
              )
            ) : (
              <span className="mr-2">Select Icon</span>
            )}
          </Button>
        </DialogTrigger>
        {value && (
          <Button type="button" variant="outline" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>

      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Choose an Icon</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="apps">Apps</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="business">Business</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>

          {Object.entries(ICON_CATEGORIES).slice(0, 3).map(([category, icons]) => (
            <TabsContent key={category} value={category}>
              <div className="grid grid-cols-6 gap-2">
                {icons.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => handleSelect(icon)}
                    className="p-3 text-3xl hover:bg-accent rounded-lg transition-colors"
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </TabsContent>
          ))}

          <TabsContent value="business">
            <div className="space-y-4">
              {Object.entries(ICON_CATEGORIES).slice(3).map(([category, icons]) => (
                <div key={category}>
                  <Label className="capitalize mb-2 block">{category}</Label>
                  <div className="grid grid-cols-6 gap-2">
                    {icons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => handleSelect(icon)}
                        className="p-3 text-3xl hover:bg-accent rounded-lg transition-colors"
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="custom">
            <div className="space-y-4">
              <div>
                <Label>Enter any emoji or character</Label>
                <Input
                  value={customIcon}
                  onChange={(e) => setCustomIcon(e.target.value)}
                  placeholder="Paste emoji or type character"
                  maxLength={4}
                />
              </div>
              <Button onClick={handleCustom} className="w-full" disabled={!customIcon.trim()}>
                Use Custom Icon
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
